import { t as tr } from "@/lib/i18n";
import { useEffect, useMemo, useState } from "react";
import { Activity, BookOpen, CheckCircle2, Lock, XCircle } from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";

import { Badge, Button, Input, Label, Sheet, SheetContent, SheetHeader, SheetTitle } from "@fox1ck-org/ui";
import {
  PROVIDER_META_BY_ID,
  SECRET_MASK,
  useCreateIntegration,
  useTestConnectionInline,
  useUpdateIntegration,
  type CredentialField,
} from "@/lib/services/integration-service";
import { Integration, IntegrationProvider } from "@gen/tracker/v1/integration_pb";
import type { ConnectionScope } from "./provider-card";

export interface SheetTarget {
  provider: IntegrationProvider;
  scope: ConnectionScope;
  editing?: boolean;
}

interface ConnectionSheetProps {
  target: SheetTarget | null;
  /** The stored row being edited / overridden, if any. */
  existing?: Integration;
  onOpenChange: (open: boolean) => void;
}

type ProbeState =
  | { kind: "idle" }
  | { kind: "running" }
  | { kind: "ok" }
  | { kind: "failed"; message: string };

// ConnectionSheet is the guided setup form for one provider connection:
// provider blurb + doc link, per-provider credential fields (secrets masked,
// stored values render as ******** and only re-send when replaced), an inline
// "Test connection" probe that runs BEFORE save, and the save itself.
export function ConnectionSheet({ target, existing, onOpenChange }: ConnectionSheetProps) {
  const create = useCreateIntegration();
  const update = useUpdateIntegration();
  const probe = useTestConnectionInline();

  const meta = target ? PROVIDER_META_BY_ID[target.provider] : undefined;
  const isEdit = !!target?.editing && !!existing;

  const [config, setConfig] = useState<Record<string, string>>({});
  const [probeState, setProbeState] = useState<ProbeState>({ kind: "idle" });

  // (Re)seed the form whenever the sheet opens for a target. Stored secret
  // fields arrive masked from the BE — keep the mask so an untouched field
  // round-trips as "keep the stored secret".
  useEffect(() => {
    if (!target) return;
    setProbeState({ kind: "idle" });
    if (isEdit && existing) {
      setConfig({ ...(existing.config as Record<string, string>) });
    } else {
      setConfig({});
    }
  }, [target, isEdit, existing]);

  const requiredMissing = useMemo(() => {
    if (!meta) return [];
    return meta.fields.filter((f) => !f.optional && !(config[f.key] ?? "").trim());
  }, [meta, config]);

  if (!target || !meta) return null;

  const setField = (key: string, value: string) => {
    setProbeState({ kind: "idle" });
    setConfig((prev) => {
      const next = { ...prev };
      if (value === "") delete next[key];
      else next[key] = value;
      return next;
    });
  };

  async function handleProbe() {
    if (!target) return;
    setProbeState({ kind: "running" });
    try {
      const res = await probe.mutateAsync({
        provider: target.provider,
        config,
        id: isEdit ? existing?.id : undefined,
      });
      setProbeState(res.ok ? { kind: "ok" } : { kind: "failed", message: res.message });
    } catch (err) {
      setProbeState({
        kind: "failed",
        message: err instanceof Error ? err.message : tr("conn.probeFailed"),
      });
    }
  }

  // No required-field check here: Save is disabled while `requiredMissing` is
  // non-empty, so the toast this used to raise could not be reached. The field
  // list is the provider's, discovered at runtime, which is why this form is a
  // config map rather than a Zod schema like the rest.
  async function handleSave() {
    if (!target || !meta) return;
    try {
      if (isEdit && existing) {
        const next = existing.clone();
        next.config = config;
        await update.mutateAsync(next);
        toast.success(tr("conn.updated", { provider: meta.label }));
      } else {
        await create.mutateAsync(
          new Integration({
            name:
              target.scope === "platform"
                ? tr("conn.platformDefault")
                : tr("conn.wsName", { provider: meta.label }),
            provider: target.provider,
            config,
            isPlatformDefault: target.scope === "platform",
          }),
        );
        toast.success(tr("conn.connected", { provider: meta.label }));
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : tr("ws.saveFailed"));
    }
  }

  const saving = create.isPending || update.isPending;

  return (
    <Sheet open={!!target} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-110 flex-col gap-0 overflow-y-auto p-0">
        <SheetHeader className="border-b border-border px-6 py-4">
          <div className="flex items-center gap-2">
            <SheetTitle className="text-lg">
              {isEdit ? meta.label : tr("conn.connectTitle", { provider: meta.label })}
            </SheetTitle>
            {target.scope === "platform" ? (
              <Badge variant="warn" className="text-xs">{tr("conn.platformDefault")}</Badge>
            ) : null}
          </div>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {target.scope === "platform"
              ? tr("conn.platform.hint")
              : meta.tagline}
          </p>
        </SheetHeader>

        <div className="flex-1 space-y-4 px-6 py-4">
          {/* Setup guidance */}
          <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3">
            <p className="text-xs leading-relaxed text-muted-foreground">{meta.setupHint}</p>
            {meta.docsUrl ? (
              <a
                href={meta.docsUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
              >
                <BookOpen className="h-3.5 w-3.5" /> {tr("conn.docs")}
              </a>
            ) : null}
          </div>

          {/* Credential fields */}
          <div className="space-y-3">
            {meta.fields.map((f) => (
              <SecretAwareField
                key={f.key}
                field={f}
                value={config[f.key] ?? ""}
                storedSecret={
                  isEdit && !!existing?.configuredSecretKeys?.includes(f.key)
                }
                onChange={(v) => setField(f.key, v)}
              />
            ))}
          </div>

          <div className="flex items-start gap-2 rounded-md border border-border bg-muted/30 p-2.5 text-xs text-muted-foreground">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              {tr("conn.encrypted")}
            </span>
          </div>

          {/* Probe result */}
          {probeState.kind === "ok" ? (
            <div className="flex items-center gap-2 rounded-md border border-ok/30 bg-ok/10 px-3 py-2 text-xs text-ok">
              <CheckCircle2 className="h-4 w-4" /> {tr("conn.verified")}
            </div>
          ) : probeState.kind === "failed" ? (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="break-all">{probeState.message}</span>
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border px-6 py-4">
          <Button
            variant="secondary"
            size="sm"
            className="gap-1.5"
            onClick={handleProbe}
            disabled={probeState.kind === "running" || requiredMissing.length > 0}
            title={
              requiredMissing.length > 0
                ? tr("conn.fillIn", { fields: requiredMissing.map((f) => f.label).join(", ") })
                : tr("conn.probe.hint")
            }
          >
            <Activity
              className={probeState.kind === "running" ? "h-3.5 w-3.5 animate-pulse" : "h-3.5 w-3.5"}
            />
            {probeState.kind === "running" ? tr("conn.testing") : tr("conn.test")}
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
              {tr("rule.ed.cancel")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={saving || requiredMissing.length > 0}
            >
              {saving ? tr("bp.saving") : isEdit ? tr("ws.save") : tr("conn.connect")}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// SecretAwareField renders one credential input. Stored secrets show the
// mask with a "Replace" affordance — typing replaces the secret, leaving it
// untouched keeps the stored value, clearing it removes the secret.
function SecretAwareField({
  field,
  value,
  storedSecret,
  onChange,
}: {
  field: CredentialField;
  value: string;
  storedSecret: boolean;
  onChange: (v: string) => void;
}) {
  const masked = field.secret && value === SECRET_MASK;
  const [replacing, setReplacing] = useState(false);

  useEffect(() => {
    setReplacing(false);
  }, [field.key, storedSecret]);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label textCase="none" className="text-xs">
          {field.label}
          {field.optional ? <span className="text-muted-foreground"> {tr("conn.optional")}</span> : null}
        </Label>
        {masked && storedSecret && !replacing ? (
          <button
            type="button"
            className="text-xs font-medium text-primary hover:underline"
            onClick={() => {
              setReplacing(true);
              onChange("");
            }}
          >
            {tr("conn.replace")}
          </button>
        ) : null}
      </div>
      <Input
        type={field.secret ? "password" : "text"}
        autoComplete="off"
        value={value}
        readOnly={masked && !replacing}
        onChange={(e) => onChange(e.target.value)}
        placeholder={
          field.secret && storedSecret && !masked ? tr("conn.newValue.ph") : field.placeholder
        }
        className={field.secret ? "font-mono text-xs" : undefined}
      />
      {field.hint ? <p className="text-xs text-muted-foreground">{field.hint}</p> : null}
    </div>
  );
}
